import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Select,
  Button,
  Space,
  Typography,
  Divider,
  Table,
  Card,
  Row,
  Col,
  Input,
  message,
  Checkbox,
  Tag
} from 'antd';
import {
  DeleteOutlined,
  MedicineBoxOutlined,
  GiftOutlined
} from '@ant-design/icons';
import axios from '../offline/profileApi';
import { isProfileQueued } from '../offline/profileActions';
import API from '../config/api';
import { ClinicService, Patient, BillingServiceItem, PatientPackage } from './type';
import { sendBillingNotificationToAllAssistants } from '../services/notificationService';
import { useClinicContext } from './ClinicContext';
import { useInventoryContext } from './InventoryContext';
import {
  AdditionalBillingSource,
  buildAdditionalBillingCatalog,
} from '../utils/additionalBillingCatalog';

const { Title, Text } = Typography;
const { Option } = Select;

interface BillingModalProps {
  visible: boolean;
  onCancel: () => void;
  patient: Patient | null;
  visitId?: string;
  onBillingComplete?: () => void;
}

interface BillingVisitType {
  type_id: string;
  name?: string;
  name_ar?: string;
  normal_price?: number;
  is_active?: boolean;
}

interface SelectedBillingItem extends BillingServiceItem {
  selection_key: string;
  source_kind: AdditionalBillingSource;
  source_id: string;
}

const BillingModal: React.FC<BillingModalProps> = ({
  visible,
  onCancel,
  patient,
  visitId,
  onBillingComplete
}) => {
  const [form] = Form.useForm();
  const [services, setServices] = useState<ClinicService[]>([]);
  const [visitTypes, setVisitTypes] = useState<BillingVisitType[]>([]);
  const [selectedServices, setSelectedServices] = useState<SelectedBillingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activePackages, setActivePackages] = useState<PatientPackage[]>([]);
  const [loadingActivePackages, setLoadingActivePackages] = useState(false);
  const [usingPackageSession, setUsingPackageSession] = useState<string | null>(null);

  const doctorId = localStorage.getItem('doctorId');
  const { selectedClinicId, selectedClinic } = useClinicContext();
  const { deductForService } = useInventoryContext();
  const catalogClinicId = selectedClinicId && selectedClinicId !== '__all_clinics__'
    ? selectedClinicId
    : patient?.clinic_id || '';
  const billingCatalog = buildAdditionalBillingCatalog(services, visitTypes);

  useEffect(() => {
    if (visible) {
      fetchBillingCatalog();
      resetForm();
    }
  }, [visible]);

  useEffect(() => {
    if (visible && patient?.patient_id && doctorId) {
      setLoadingActivePackages(true);
      axios.get(`${API.BASE_URL}${API.ENDPOINTS.PATIENT_ACTIVE_PACKAGES(patient.patient_id)}`, { params: { doctor_id: doctorId } })
        .then(res => {
          if (res.data?.success) setActivePackages(res.data.data || []);
        })
        .catch(() => setActivePackages([]))
        .finally(() => setLoadingActivePackages(false));
    } else {
      setActivePackages([]);
    }
  }, [visible, patient?.patient_id, doctorId]);

  const resetForm = () => {
    form.resetFields();
    setSelectedServices([]);
    form.setFieldsValue({
      paymentMethod: 'cash',
      paymentStatus: 'pending' // Default to pending - assistant will collect payment
    });
  };

  const fetchBillingCatalog = async () => {
    if (!doctorId) return;

    try {
      setLoading(true);
      const params = catalogClinicId ? { clinic_id: catalogClinicId } : {};
      const [serviceResponse, visitTypeResponse] = await Promise.all([
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_SERVICES(doctorId)}`, { params })
          .catch(error => {
            console.error('Error fetching services:', error);
            return null;
          }),
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.VISIT_TYPE_CONFIG(doctorId)}`, { params })
          .catch(error => {
            console.error('Error fetching visit types:', error);
            return null;
          })
      ]);

      setServices(serviceResponse?.data?.success ? serviceResponse.data.data || [] : []);
      setVisitTypes(visitTypeResponse?.data?.success
        ? visitTypeResponse.data.data?.visit_types || []
        : []);
    } finally {
      setLoading(false);
    }
  };

  const handleUsePackageSession = async (patientPackageId: string) => {
    setUsingPackageSession(patientPackageId);
    try {
      const res = await axios.post(
        `${API.BASE_URL}${API.ENDPOINTS.USE_PACKAGE_SESSION(patientPackageId)}`,
        { visit_id: visitId || '' }
      );
      if (res.data?.success) {
        message.success('Package session used – patient not charged');

        // Auto-deduct consumables for the package's linked service
        const pkg = activePackages.find(p => p.patient_package_id === patientPackageId);
        if (pkg?.service_id && patient) {
          try {
            const svcRes = await axios.get(`${API.BASE_URL}${API.ENDPOINTS.DOCTOR_SERVICES(doctorId)}`);
            if (svcRes.data?.success) {
              const svc = (svcRes.data.data as ClinicService[]).find(s => s.service_id === pkg.service_id);
              if (svc?.consumables && svc.consumables.length > 0) {
                const deductResult = await deductForService({
                  consumables: svc.consumables.map(c => ({
                    inventory_id: c.inventory_id,
                    quantity: c.quantity_per_use,
                    notes: `Package session: ${pkg.package_name}`
                  })),
                  patient_id: patient.patient_id,
                  patient_name: patient.patient_name,
                  service_id: svc.service_id,
                  service_name: svc.name,
                  package_id: patientPackageId,
                  package_name: pkg.package_name || '',
                  action_type: 'package'
                });
                if (deductResult.errors.length > 0) {
                  message.warning(`Package session recorded. Stock warning: ${deductResult.errors.map((e: any) => e.name || e.inventory_id).join(', ')} had insufficient stock.`);
                }
              }
            }
          } catch (invErr) {
            console.warn('Inventory deduction for package session failed (non-blocking):', invErr);
          }
        }

        setActivePackages(prev => {
          const updated = res.data.data;
          if (updated.remaining_sessions === 0) return prev.filter(p => p.patient_package_id !== patientPackageId);
          return prev.map(p => p.patient_package_id === patientPackageId ? { ...p, ...updated } : p);
        });
        onBillingComplete?.();
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to use package session');
    } finally {
      setUsingPackageSession(null);
    }
  };

  const handleRemoveService = (selectionKey: string) => {
    setSelectedServices(selectedServices.filter(s => s.selection_key !== selectionKey));
  };

  const handleServiceSelectionChange = (selectionKeys: string[]) => {
    setSelectedServices(prev => {
      const existingById = new Map(prev.map(service => [service.selection_key, service]));

      return selectionKeys
        .map(selectionKey => {
          const existing = existingById.get(selectionKey);
          if (existing) return existing;

          const item = billingCatalog.find(candidate => candidate.selection_key === selectionKey);
          if (!item) return null;

          return {
            selection_key: item.selection_key,
            source_kind: item.source_kind,
            source_id: item.source_id,
            service_id: item.service_id,
            service_name: item.service_name,
            price: item.price,
            quantity: 1,
            subtotal: item.price
          };
        })
        .filter((service): service is SelectedBillingItem => Boolean(service));
    });
  };

  const handleQuantityChange = (selectionKey: string, quantity: number) => {
    const updated = selectedServices.map(s => {
      if (s.selection_key === selectionKey) {
        return {
          ...s,
          quantity,
          subtotal: s.price * quantity
        };
      }
      return s;
    });
    setSelectedServices(updated);
  };

  // Calculate totals - NO consultation fee (already paid on patient arrival)
  const servicesTotal = selectedServices.reduce((sum, s) => sum + s.subtotal, 0);
  const subtotal = servicesTotal;
  const totalAmount = subtotal;

  const handleSubmit = async () => {
    if (!doctorId || !patient) {
      message.error('Missing required information');
      return;
    }

    if (selectedServices.length === 0) {
      message.warning('Please add at least one service');
      return;
    }

    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const billedItems: BillingServiceItem[] = selectedServices.map(({
        selection_key: _selectionKey,
        source_kind: _sourceKind,
        source_id: _sourceId,
        ...item
      }) => item);

      // Create billing for additional services ONLY (no consultation fee)
      const billingData = {
        doctor_id: doctorId,
        patient_id: patient.patient_id,
        patient_name: patient.patient_name,
        patient_phone: patient.patient_phone,
        patient_file_number: patient.file_number || patient.file_id || '',
        visit_id: visitId || '',
        clinic_id: catalogClinicId,
        consultationFee: 0, // Consultation is already paid
        consultationType: 'خدمات إضافية', // Additional services
        services: billedItems,
        discount: null,
        paymentStatus: values.paymentStatus,
        paymentMethod: values.paymentMethod,
        amountPaid: values.paymentStatus === 'paid' ? totalAmount : (values.amountPaid || 0),
        notes: values.notes || 'Additional services bill'
      };

      const offlineInventory = selectedServices.flatMap(billed => {
        if (billed.source_kind !== 'service') return [];
        const service = services.find(item => item.service_id === billed.source_id);
        return (service?.consumables || []).map(item => ({ inventory_id: item.inventory_id, quantity: item.quantity_per_use * billed.quantity }));
      });
      const response = await axios.post(`${API.BASE_URL}${API.ENDPOINTS.BILLING}`, billingData, { offlineInventory } as any);

      if (response.data.success) {
        message.success('Services bill created successfully');

        // Auto-deduct consumables for each billed service
        try {
          for (const billedSvc of selectedServices) {
            if (billedSvc.source_kind !== 'service') continue;
            const fullSvc = services.find(s => s.service_id === billedSvc.source_id);
            if (fullSvc?.consumables && fullSvc.consumables.length > 0) {
              const consumablesToDeduct = fullSvc.consumables.map(c => ({
                inventory_id: c.inventory_id,
                quantity: c.quantity_per_use * billedSvc.quantity,
                notes: `Billed service: ${fullSvc.name} x${billedSvc.quantity}`
              }));
              const deductResult = await deductForService({
                consumables: consumablesToDeduct,
                patient_id: patient.patient_id,
                patient_name: patient.patient_name,
                service_id: fullSvc.service_id,
                service_name: fullSvc.name,
                action_type: 'service'
              });
              if (deductResult.errors.length > 0) {
                message.warning(`Bill saved. Stock warning for "${fullSvc.name}": ${deductResult.errors.map((e: any) => e.name || e.inventory_id).join(', ')} had insufficient stock.`);
              }
            }
          }
        } catch (invErr) {
          console.warn('Inventory auto-deduction failed (non-blocking):', invErr);
          message.warning('Bill created, but inventory auto-deduction failed. Please update stock manually.');
        }

        // Send notification to assistant about the additional services
        // Also saves billing to patient document for real-time display
        try {
          // Use patient's date (visit date) in the same format as Firebase path (yyyy-M-d)
          // If patient.date is in different format, parse it
          let dateString = patient.date || '';

          // If date is in format "2026-1-31" or "2026-01-31", use as is
          // If in other format like "2026-01-31T00:00:00Z", extract date part
          if (dateString.includes('T')) {
            dateString = dateString.split('T')[0];
          }

          // Ensure format is yyyy-M-d (single digit month/day if needed)
          if (dateString && dateString.match(/^\d{4}-\d{1,2}-\d{1,2}$/)) {
            // Format is already correct, but ensure single digits for month/day
            const parts = dateString.split('-');
            dateString = `${parts[0]}-${parseInt(parts[1])}-${parseInt(parts[2])}`;
          }

          // Fallback to today if patient.date is missing
          if (!dateString) {
            const today = new Date();
            dateString = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
          }

          console.log('📅 Using patient date for billing:', dateString);

          await sendBillingNotificationToAllAssistants({
            doctor_id: doctorId,
            clinic_id: catalogClinicId,
            patient_id: patient.patient_id, // Required for saving to patient document
            date: dateString, // Required for Firebase path - use patient's visit date
            patient_name: patient.patient_name,
            totalAmount: totalAmount,
            amountPaid: values.paymentStatus === 'paid' ? totalAmount : (values.amountPaid || 0),
            paymentStatus: values.paymentStatus,
            paymentMethod: values.paymentMethod,
            consultationFee: 0,
            consultationType: 'خدمات إضافية',
            servicesTotal: servicesTotal,
            services: billedItems,
            billing_id: response.data.data?.billing_id || '',
            clinic_name: selectedClinic?.name || '',
            doctor_name: '',
            notes: values.notes || ''
          });
          message.success('Notification sent to assistant');
        } catch (notifError) {
          console.error('Failed to send notification:', notifError);
          message.warning('Bill created but notification failed. Please notify assistant manually!');
        }

        onBillingComplete?.();
        onCancel();
      }
    } catch (error) {
      if (isProfileQueued(error)) { message.warning('Billing saved on this device — pending sync. It is not yet a confirmed server bill.'); onCancel(); return; }
      console.error('Error creating billing:', error);
      message.error('Failed to create billing record');
    } finally {
      setSubmitting(false);
    }
  };

  const serviceColumns = [
    {
      title: 'Service',
      dataIndex: 'service_name',
      key: 'service_name'
    },
    {
      title: 'Source',
      dataIndex: 'source_kind',
      key: 'source_kind',
      render: (source: AdditionalBillingSource) => (
        <Tag color={source === 'visit_type' ? 'blue' : 'green'}>
          {source === 'visit_type' ? 'Visit Type' : 'Service'}
        </Tag>
      )
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => `${price.toLocaleString()} EGP`
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      render: (qty: number, record: SelectedBillingItem) => (
        <InputNumber
          min={1}
          max={100000}
          value={qty}
          size="small"
          onChange={(val) => handleQuantityChange(record.selection_key, val || 1)}
        />
      )
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      key: 'subtotal',
      render: (subtotal: number) => (
        <Text strong style={{ color: '#52c41a' }}>{subtotal.toLocaleString()} EGP</Text>
      )
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_: any, record: SelectedBillingItem) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveService(record.selection_key)}
        />
      )
    }
  ];

  if (!patient) return null;

  return (
    <Modal
      title={
        <Space>
          <MedicineBoxOutlined style={{ color: '#1890ff' }} />
          <span>Add Bill for {patient.patient_name}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={700}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={handleSubmit}
          disabled={selectedServices.length === 0}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        >
          Create Bill ({totalAmount.toLocaleString()} EGP)
        </Button>
      ]}
    >
      {activePackages.length > 0 && (
        <Card title={<Space><GiftOutlined /> Use package session (no charge)</Space>} size="small" style={{ marginBottom: 16, backgroundColor: '#f6ffed' }}>
          {loadingActivePackages ? (
            <Text type="secondary">Loading packages...</Text>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }}>
              {activePackages.map(pp => (
                <div key={pp.patient_package_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <Text>{pp.package_name} – Remaining: {pp.remaining_sessions}</Text>
                  <Button
                    type="primary"
                    size="small"
                    loading={usingPackageSession === pp.patient_package_id}
                    disabled={pp.remaining_sessions <= 0}
                    onClick={() => handleUsePackageSession(pp.patient_package_id)}
                    style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                  >
                    Use 1 session
                  </Button>
                </div>
              ))}
            </Space>
          )}
        </Card>
      )}

      <Form form={form} layout="vertical">
        {/* Services and visit types are both billed as additional service lines. */}
        <Card title="Select Services or Visit Types" size="small" style={{ marginBottom: 16 }}>
          {loading ? (
            <Text type="secondary">Loading services...</Text>
          ) : (
            <Checkbox.Group
              value={selectedServices.map(service => service.selection_key)}
              onChange={(values) => handleServiceSelectionChange(values as string[])}
              style={{ width: '100%', marginBottom: 16 }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {billingCatalog.map(item => (
                  <Checkbox key={item.selection_key} value={item.selection_key}>
                    <Text strong>{item.service_name}</Text>
                    <Text type="secondary"> - {item.price.toLocaleString()} EGP</Text>
                    <Tag color={item.source_kind === 'visit_type' ? 'blue' : 'green'} style={{ marginInlineStart: 8 }}>
                      {item.source_kind === 'visit_type' ? 'Visit Type' : 'Service'}
                    </Tag>
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          )}

          {selectedServices.length > 0 ? (
            <Table
              dataSource={selectedServices}
              columns={serviceColumns}
              rowKey="selection_key"
              pagination={false}
              size="small"
            />
          ) : (
            <Text type="secondary">No billing items selected yet</Text>
          )}
        </Card>

        {/* Payment Section */}
        <Card title="Payment" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="paymentMethod"
                label="Payment Method"
                initialValue="cash"
              >
                <Select>
                  <Option value="cash">💵 Cash</Option>
                  <Option value="card">💳 Card</Option>
                  <Option value="insurance">🏥 Insurance</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="paymentStatus"
                label="Payment Status"
                initialValue="pending"
              >
                <Select>
                  <Option value="pending">⏳ Pending (Assistant will collect)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Additional notes..." />
          </Form.Item>
        </Card>

        {/* Summary */}
        <Card
          size="small"
          style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}
        >
          <Title level={5} style={{ margin: 0, marginBottom: 8 }}>Bill Summary</Title>
          <Row justify="space-between">
            <Text>Services Total:</Text>
            <Text>{servicesTotal.toLocaleString()} EGP</Text>
          </Row>
          <Divider style={{ margin: '8px 0' }} />
          <Row justify="space-between">
            <Title level={4} style={{ margin: 0 }}>Total:</Title>
            <Title level={4} style={{ margin: 0, color: '#52c41a' }}>
              {totalAmount.toLocaleString()} EGP
            </Title>
          </Row>
        </Card>
      </Form>
    </Modal>
  );
};

export default BillingModal;
